import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Search } from 'lucide-react'
import { AccountingSyncChip } from '@/components/AccountingSyncChip'
import { Button } from '@/components/ui/button'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { OwnerFilter } from '@/components/OwnerFilter'
import { Input } from '@/components/ui/input'
import { getJSON } from '@/lib/api'
import { ViewOnlyBadge } from '@/components/templates/app-shell'
import { displayShortDate } from '@/lib/dates'
import {
  AgingReportBody,
  type AgingContactDrillDown,
  type AgingReportResponse,
  type AgingSortState,
  nextAgingSort,
} from '@/pages/AgingReportTable'
export type { AgingContactDrillDown } from '@/pages/AgingReportTable'

interface MembershipLevel {
  id: number
  name: string
}

interface MembershipLevelsResponse {
  membership_levels: MembershipLevel[]
}

export function AgingReportPage({
  onDrillDown,
}: {
  onDrillDown: (drillDown: AgingContactDrillDown) => void
}) {
  const [search, setSearch] = React.useState('')
  const [membershipLevelIds, setMembershipLevelIds] = React.useState<Set<string>>(new Set())
  const [sort, setSort] = React.useState<AgingSortState | null>(null)
  const [owner, setOwner] = React.useState<string | undefined>()
  const query = agingReportQuery(search, membershipLevelIds, sort, owner)
  const reportQuery = useQuery({
    queryKey: ['aging-report', query],
    queryFn: () => getJSON<AgingReportResponse>(`/api/v1/aging_report${query}`),
    retry: false,
  })
  const membershipLevelsQuery = useQuery({
    queryKey: ['membership-levels', 'aging-report'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const report = reportQuery.data?.aging_report

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold">A/R aging</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">
          {report ? `As of ${displayDate(report.as_of)} · Aged by due date` : 'Aged by due date'}
        </p>
      </header>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <Input
              className="h-8 pl-9"
              value={search}
              placeholder="Search contacts"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <FilterDropdown
            label="Level"
            emptyLabel="All levels"
            options={(membershipLevelsQuery.data?.membership_levels ?? []).map((level) => ({
              value: String(level.id),
              label: level.name,
            }))}
            selected={membershipLevelIds}
            onChange={setMembershipLevelIds}
          />
          <OwnerFilter value={owner} onChange={setOwner} />
        </div>
        <Button size="sm" variant="outline" asChild>
          <a href="/api/v1/aging_report/export" download>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </a>
        </Button>
      </div>
      <div className="mt-4 mb-2 flex items-center justify-between gap-3">
        <p className="text-caption text-muted-foreground">
          {report ? contactCount(report.rows.length) : '— contacts with open balances'}
        </p>
        <AccountingSyncChip />
      </div>
      <AgingReportBody
        query={reportQuery}
        isFiltered={search.trim().length > 0 || membershipLevelIds.size > 0}
        sort={sort}
        onSort={(key) => setSort(nextAgingSort(sort, key))}
        onDrillDown={onDrillDown}
      />
    </div>
  )
}

function agingReportQuery(
  search: string,
  membershipLevelIds: ReadonlySet<string>,
  sort: AgingSortState | null,
  owner?: string,
) {
  const params = new URLSearchParams()
  if (search.trim()) params.set('search', search.trim())
  Array.from(membershipLevelIds)
    .sort()
    .forEach((id) => params.append('membership_level_ids[]', id))
  if (sort) {
    params.set('sort', sort.key)
    params.set('direction', sort.direction)
  }
  if (owner) params.set('owner', owner)
  const query = params.toString()
  return query ? `?${query}` : ''
}

const displayDate = displayShortDate

function contactCount(count: number) {
  return `${count} ${count === 1 ? 'contact' : 'contacts'} with open balances`
}
